import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.authService.isLoggedIn) {
      // Check if route has required roles
      const requiredRoles = route.data['roles'] as string[];
      if (requiredRoles && requiredRoles.length > 0) {
        const userRole = this.authService.currentUser?.role;
        if (!userRole || !requiredRoles.includes(userRole)) {
          // User doesn't have required role, redirect to their dashboard
          this.authService.navigateByRole();
          return false;
        }
      }
      return true;
    }

    // Not logged in, redirect to login
    this.router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class NoAuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): boolean {
    if (this.authService.isLoggedIn) {
      // Already logged in, redirect to dashboard
      this.authService.navigateByRole();
      return false;
    }
    return true;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): boolean {
    if (this.authService.isLoggedIn && this.authService.currentUser?.role === 'admin') {
      return true;
    }

    if (this.authService.isLoggedIn) {
      this.authService.navigateByRole();
    } else {
      this.router.navigate(['/auth/login']);
    }
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ResellerGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): boolean {
    const role = this.authService.currentUser?.role;
    if (this.authService.isLoggedIn && (role === 'admin' || role === 'reseller')) {
      return true;
    }

    if (this.authService.isLoggedIn) {
      this.authService.navigateByRole();
    } else {
      this.router.navigate(['/auth/login']);
    }
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AgentGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): boolean {
    const role = this.authService.currentUser?.role;
    // Only agent role can access agent routes
    if (this.authService.isLoggedIn && role === 'agent') {
      return true;
    }

    if (this.authService.isLoggedIn) {
      // Redirect to their own dashboard
      this.authService.navigateByRole();
    } else {
      this.router.navigate(['/auth/login']);
    }
    return false;
  }
}
